import React, { useState } from "react";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Sector,
} from "recharts";

const PaymentMode = ({
    digitalPayment,
    cashPayment,
    selectpaymenttype,
    setSelectpaymenttype,
    serviceTypes
}) => {
    const [selectedCategory, setSelectedCategory] = useState("All"); // Default: All
    const [activeIndex, setActiveIndex] = useState(0);

    const totalPayment = digitalPayment + cashPayment;
    const data = [
        { name: "Digital", value: digitalPayment, color: "#003A71" },
        { name: "Cash", value: cashPayment, color: "#FBB927" },
    ];

    const handleCategoryClick = (category) => {
        setSelectedCategory(category);
    };

    // Custom Active Shape Renderer
    const renderActiveShape = (props) => {
        const RADIAN = Math.PI / 180;
        const {
            cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle,
            fill, payload, percent, value
        } = props;
        const sin = Math.sin(-RADIAN * midAngle);
        const cos = Math.cos(-RADIAN * midAngle);
        const sx = cx + (outerRadius + 10) * cos;
        const sy = cy + (outerRadius + 10) * sin;
        const mx = cx + (outerRadius + 30) * cos;
        const my = cy + (outerRadius + 30) * sin;
        const ex = mx + (cos >= 0 ? 1 : -1) * 22;
        const ey = my;
        const textAnchor = cos >= 0 ? "start" : "end";

        return (
            <g>
                <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill} className="font-semibold">
                    {payload.name}
                </text>
                <Sector
                    cx={cx}
                    cy={cy}
                    innerRadius={innerRadius}
                    outerRadius={outerRadius}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    fill={fill}
                />
                <Sector
                    cx={cx}
                    cy={cy}
                    innerRadius={outerRadius + 6}
                    outerRadius={outerRadius + 10}
                    startAngle={startAngle}
                    endAngle={endAngle}
                    fill={fill}
                />
                <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
                <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
                <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} textAnchor={textAnchor} fill="#333">
                    {`₹${value.toLocaleString()}`}
                </text>
                <text x={ex + (cos >= 0 ? 1 : -1) * 12} y={ey} dy={18} textAnchor={textAnchor} fill="#999">
                    {(percent * 100).toFixed(1)}%
                </text>
            </g>
        );
    };

    return (
        <div className="bg-white rounded-xl shadow p-6 flex gap-6">
            {/* Left Summary Section - 30% */}
            <div className="w-[30%] space-y-4">
                <h3 className="text-md font-semibold text-[#003A71]">Summary</h3>

                <div
                    className={`p-3 rounded-lg border cursor-pointer ${selectedCategory === "All"
                        ? "border-blue-400 bg-blue-50"
                        : "border-blue-100 bg-white hover:bg-blue-50"
                        }`}
                    onClick={() => handleCategoryClick("All")}
                >
                    <div className="text-sm text-gray-600">Total Revenue</div>
                    <div className="text-xl font-bold text-[#003A71]">
                        ₹{totalPayment.toLocaleString()}
                    </div>
                </div>

                <div
                    className={`p-3 rounded-lg border cursor-pointer ${selectedCategory === "Digital"
                        ? "border-blue-400 bg-blue-50"
                        : "border-blue-100 bg-white hover:bg-blue-50"
                        }`}
                    onClick={() => handleCategoryClick("Digital")}
                >
                    <div className="text-sm text-gray-600">Digital</div>
                    <div className="text-xl font-bold text-[#003A71]">
                        ₹{digitalPayment.toLocaleString()}
                    </div>
                </div>

                <div
                    className={`p-3 rounded-lg border cursor-pointer ${selectedCategory === "Cash"
                        ? "border-yellow-400 bg-yellow-50"
                        : "border-yellow-100 bg-white hover:bg-yellow-50"
                        }`}
                    onClick={() => handleCategoryClick("Cash")}
                >
                    <div className="text-sm text-gray-600">Cash</div>
                    <div className="text-xl font-bold text-[#FBB927]">
                        ₹{cashPayment.toLocaleString()}
                    </div>
                </div>
            </div>

            {/* Right Chart Section - 70% */}
            <div className="w-[70%]">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-md font-semibold text-[#003A71]">Payment Mode</h3>
                </div>

                {/* Week-style buttons */}
                <div className="flex gap-2 mb-4">
                    <button
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${selectpaymenttype === ""
                            ? "bg-blue-500 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-blue-100"
                            }`}
                        onClick={() => setSelectpaymenttype("")}
                    >
                        All
                    </button>

                    {/* Map over serviceTypes */}
                    {serviceTypes.map((type) => (
                        <button
                            key={type}
                            className={`px-4 py-2 rounded-lg text-sm font-medium ${selectpaymenttype === type
                                ? "bg-blue-500 text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-blue-100"
                                }`}
                            onClick={() => setSelectpaymenttype(type)}
                        >
                            {type}
                        </button>
                    ))}
                </div>


                {/* Pie Chart */}
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            activeIndex={activeIndex}
                            activeShape={renderActiveShape}
                            data={data}
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={100}
                            dataKey="value"
                            onMouseEnter={(_, index) => setActiveIndex(index)}
                        >
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default PaymentMode;
